import { twJoin } from "tailwind-merge";
import type { GraphNode as NodeType, SocketIn } from "../graph/node-socket-connection";
import { NodeEvents, SocketEvents } from "../types/IEventorListners";

interface TextSelectorSerializedState {
	highlights: Array<{ start: number; end: number }>;
}

export class TextSelector {
	dom: {
		textContainer: HTMLDivElement;
	};
	private highlights: Array<{ start: number; end: number }> = [];
	private value = "";
	constructor(variant: "selected" | "default" = "default") {
		this.dom = {
			textContainer: document.createElement("div")
		};
		this.applyStyles(variant);
	}

	applyStyles(variant: "selected" | "default") {
		this.dom.textContainer.className = `textSelectorContainer ${variant}`;
	}

	domElement() {
		return this.dom.textContainer;
	}

	onTextChange(text: string) {
		if (this.value !== text) {
			this.removeAllHighlights();
		}
		this.value = text;
	}

	serialize(): TextSelectorSerializedState {
		return {
			highlights: [...this.highlights]
		};
	}

	restore(state: TextSelectorSerializedState) {
		this.highlights = state.highlights;
		this.renderHighlights();
	}

	/**
	 * Add a highlight from the start to end position
	 * This preserves existing highlights
	 */
	addHighlight(start: number, end: number) {
		const text = this.value;
		if (start < 0 || end > text.length || start >= end) return this;

		// Check for overlaps with existing highlights
		const hasOverlap = this.highlights.some((h) => start < h.end && end > h.start);

		if (hasOverlap) {
			console.warn(
				"New highlight overlaps with existing highlights. This may cause rendering issues."
			);
			return;
		}

		// Add new highlight to the collection
		this.highlights.push({
			start,
			end
		});

		// Sort highlights by start position to ensure proper rendering
		this.highlights.sort((a, b) => a.start - b.start);

		// Render all highlights
		this.renderHighlights();

		return this;
	}

	/**
	 * Highlight the currently selected text in the textarea
	 * This preserves existing highlights
	 */
	highlightSelectedText(textarea: HTMLTextAreaElement) {
		if (!textarea) return this;

		const start = textarea.selectionStart;
		const end = textarea.selectionEnd;

		if (start !== end) {
			this.addHighlight(start, end);
		}

		return this;
	}

	/**
	 * Remove all highlighted text and restore plain text
	 */
	removeAllHighlights() {
		this.highlights = [];

		this.dom.textContainer.textContent = this.value;

		return this;
	}

	/**
	 * Remove a specific highlight by its index
	 */
	removeHighlight(index: number) {
		if (index >= 0 && index < this.highlights.length) {
			this.highlights.splice(index, 1);
			this.renderHighlights();
		}
		return this;
	}

	/**
	 * Render all current highlights
	 * This is called internally when highlights change
	 */
	private renderHighlights() {
		const text = this.value;

		// Clear the container
		this.dom.textContainer.innerHTML = "";

		if (this.highlights.length === 0) {
			this.dom.textContainer.textContent = text;
			return;
		}

		let lastIndex = 0;

		// Process each highlight
		for (const highlight of this.highlights) {
			// Add text before the highlight
			if (highlight.start > lastIndex) {
				const beforeText = document.createTextNode(
					text.substring(lastIndex, highlight.start)
				);
				this.dom.textContainer.appendChild(beforeText);
			}

			// Add highlighted text
			const highlightSpan = document.createElement("span");
			highlightSpan.className = twJoin("rounded-sm bg-[#FFED23]");
			highlightSpan.textContent = text.substring(highlight.start, highlight.end);
			this.dom.textContainer.appendChild(highlightSpan);

			lastIndex = highlight.end;
		}

		// Add text after the last highlight
		if (lastIndex < text.length) {
			const afterText = document.createTextNode(text.substring(lastIndex));
			this.dom.textContainer.appendChild(afterText);
		}
	}
}

export class TextSelectorManager {
	constructor(
		public node: NodeType,
		public socket: SocketIn,
		public textArea: HTMLTextAreaElement,
		public textSelector: TextSelector
	) {
		this.restoreInitState();
		this.initListners();
	}
	restoreInitState() {
		if (this.node.meta[this.getMetaKey()]) {
			this.textSelector.restore(this.node.meta[this.getMetaKey()]);
		}
	}
	initListners() {
		this.node.addListener(NodeEvents.PreSavePreparing, () => {
			this.node.meta[this.getMetaKey()] = this.textSelector.serialize();
		});
		this.socket.addListener(SocketEvents.Change, () => {
			this.textSelector.onTextChange(this.socket.tryGetValue());
		});
		this.textArea.addEventListener("input", (e) => {
			this.textSelector.onTextChange((e.target as HTMLInputElement).value || "");
		});
		this.textArea.addEventListener("keydown", (e) => {
			if (e.code === "KeyH" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
				this.textSelector.highlightSelectedText(this.textArea);
				e.preventDefault();
				e.stopPropagation();
			}
		});
	}

	getMetaKey(): string {
		return `${this.socket.name}textSelector`;
	}
}
